import React from 'react'
import { Box, Typography, makeStyles } from '@material-ui/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const useStyles = makeStyles((theme) => ({
  // --- Horizontal Stepper ---
  stepperBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 0',
    gap: 0
  },
    stepWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1,
      cursor: 'pointer'
    },
  stepNum: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: theme.palette.grey[300],
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    zIndex: 2,
    transition: 'all 0.2s'
  },
  stepNumActive: {
    background: theme.palette.primary.main,
    color: '#fff',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
  },
  stepNumCompleted: {
    background: '#4caf50',
    color: '#fff'
  },
  stepLabel: {
    fontSize: 11,
    marginTop: 4,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    whiteSpace: 'nowrap'
  },
  stepLabelActive: {
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  connector: {
    position: 'absolute',
    top: 14,
    left: 'calc(50% + 18px)',
    right: 'calc(-50% + 18px)',
    height: 2,
    background: theme.palette.grey[300],
    zIndex: 1
  },
  connectorCompleted: {
    background: '#4caf50'
  },
  // --- Content ---
  content: {
    padding: '16px 0'
  },
  // --- Footer ---
  footer: {
    padding: '16px 0 0',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  // --- Progress Dots ---
  progressDots: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
    dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.grey[400],
    transition: 'all 0.2s',
    cursor: 'default'
  },
  dotActive: {
    background: theme.palette.primary.main,
    transform: 'scale(1.3)'
  },
  dotCompleted: {
    background: '#4caf50'
  }
}))

/**
 * Horizontal stepper for MPCI forms.
 *
 * Props:
 *   steps        {Array}   - [{ label: string }]
 *   activeStep   {number}  - 0-indexed current step
 *   onStepChange {fn}      - (stepIndex) => void
 *   completed    {Array}   - boolean[] for completed steps
 *   children     {node}    - step content below stepper
 *   footer       {node}    - custom footer (Back/Next buttons)
 */
function MpciStepper({ steps, activeStep, onStepChange, completed = [], children, footer }) {
  const classes = useStyles()

  return (
    <Box>
      {/* Horizontal step indicators */}
      <Box className={classes.stepperBar}>
        {steps.map((step, index) => {
          const isActive = index === activeStep
          const isCompleted = completed[index] === true || index < activeStep
          const isLast = index === steps.length - 1

          return (
            <Box key={index} className={classes.stepWrapper} onClick={() => onStepChange && onStepChange(index)}>
              {/* Connector line (not on last) */}
              {!isLast && (
                <Box className={`${classes.connector} ${isCompleted ? classes.connectorCompleted : ''}`} />
              )}
              {/* Number circle */}
              <Box className={`${classes.stepNum} ${isActive ? classes.stepNumActive : ''} ${isCompleted && !isActive ? classes.stepNumCompleted : ''}`}>
                {isCompleted && !isActive ? (
                  <FontAwesomeIcon icon={['fal', 'check']} style={{ fontSize: 12 }} />
                ) : (
                  index + 1
                )}
              </Box>
              {/* Label */}
              <Typography className={`${classes.stepLabel} ${isActive ? classes.stepLabelActive : ''}`}>
                {step.label}
              </Typography>
            </Box>
          )
        })}
      </Box>

      {/* Step content */}
      <Box className={classes.content}>
        {children}
      </Box>

      {/* Footer with navigation */}
      {footer && (
        <Box className={classes.footer}>
          {footer}
        </Box>
      )}
    </Box>
  )
}

/**
 * Progress dots for bottom navigation
 */
function ProgressDots({ total, activeStep, onDotClick }) {
  const classes = useStyles()

  return (
    <Box className={classes.progressDots}>
      {Array.from({ length: total }).map((_, i) => (
                <Box
          key={i}
          className={`${classes.dot} ${i === activeStep ? classes.dotActive : ''} ${i < activeStep ? classes.dotCompleted : ''}`}
        />
      ))}
    </Box>
  )
}

export { MpciStepper, ProgressDots }
export default MpciStepper
